import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Upload, ArrowLeft } from 'lucide-react';
import { audioApi } from '@/lib/api';
import { AUDIO_COLORS, AUDIO_SHAPES, LANGUAGE_FLAGS, COMMON_EMOJIS } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';

// Página que recibe audios compartidos desde otras apps (WhatsApp, Telegram, etc.)
const CompartirPage = () => {
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Clasificación
  const [shape, setShape] = useState('circle');
  const [color, setColor] = useState('pink'); // Humor por defecto para memes
  const [emoji, setEmoji] = useState('😂');
  const [language, setLanguage] = useState('es');

  // Recibir archivo compartido desde el Service Worker o URL params
  useEffect(() => {
    checkSharedFile();
  }, []);

  const checkSharedFile = async () => {
    // Método 1: Verificar si hay archivo en el cache del SW
    if ('caches' in window) {
      try {
        const cache = await caches.open('shared-audio');
        const keys = await cache.keys();
        if (keys.length > 0) {
          const response = await cache.match(keys[0]);
          if (response) {
            const blob = await response.blob();
            processAudioFile(blob);
            // Limpiar cache
            await cache.delete(keys[0]);
          }
        }
      } catch (e) {
        console.log('No cached shared file');
      }
    }

    // Método 2: Verificar localStorage (backup)
    const pendingAudio = localStorage.getItem('pendingSharedAudio');
    if (pendingAudio) {
      try {
        const data = JSON.parse(pendingAudio);
        // Convertir base64 a blob
        const response = await fetch(data.dataUrl);
        const blob = await response.blob();
        processAudioFile(blob);
        localStorage.removeItem('pendingSharedAudio');
      } catch (e) {
        console.log('No pending audio in localStorage');
      }
    }
  };

  const processAudioFile = (file) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    
    audio.onloadedmetadata = () => {
      const fileDuration = Math.round(audio.duration);
      
      if (fileDuration > 59) {
        setError('El audio es muy largo (máx 59s). Recórtalo antes.');
        return;
      }
      
      setDuration(fileDuration);
      setAudioFile(file);
      setAudioUrl(url);
    };

    audio.onerror = () => {
      setError('No se pudo leer el audio');
    };
  };

  const handleManualUpload = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('audio/')) {
      processAudioFile(file);
    }
  };

  const handleUpload = async () => {
    if (!audioFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioFile, `shared_${Date.now()}.webm`);
      formData.append('shape', shape);
      formData.append('color', color);
      formData.append('emoji', emoji);
      formData.append('language', language);
      formData.append('duration_seconds', duration.toString());
      formData.append('allow_replies', 'true');

      await audioApi.uploadAudio(formData);
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al publicar');
    } finally {
      setUploading(false);
    }
  };

  // No autenticado
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="text-6xl mb-4">📲</div>
          <h2 className="text-xl font-bold text-white mb-2">Sube a AudioToo</h2>
          <p className="text-white/60 mb-6">Inicia sesión para subir este audio</p>
          <button 
            onClick={login}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-medium"
          >
            Entrar con Google
          </button>
        </div>
      </div>
    );
  }

  // Éxito
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-6">
            <Check size={48} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">¡Subido!</h2>
          <p className="text-white/60">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  const colorData = AUDIO_COLORS[color];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-white/60 p-2 -ml-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-white font-bold">Subir Audio</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-200 p-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Si no hay audio, mostrar selector */}
        {!audioFile ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📲</div>
            <h2 className="text-xl font-bold text-white mb-2">Comparte un audio aquí</h2>
            <p className="text-white/60 mb-6 text-sm">
              Desde WhatsApp, Telegram o cualquier app, pulsa "Compartir" y elige AudioToo
            </p>
            
            <label className="block">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20 cursor-pointer hover:bg-white/15">
                <Upload size={32} className="text-white/60 mx-auto mb-3" />
                <p className="text-white font-medium">O selecciona un archivo</p>
                <p className="text-white/50 text-sm mt-1">MP3, WAV, M4A (máx 59s)</p>
              </div>
              <input 
                type="file" 
                accept="audio/*"
                className="hidden"
                onChange={handleManualUpload}
              />
            </label>
          </div>
        ) : (
          <>
            {/* Preview del audio */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <p className="text-green-400 font-medium mb-2">✓ Audio recibido</p>
              <p className="text-white/60 text-sm">{duration} segundos</p>
              {audioUrl && (
                <audio controls src={audioUrl} className="w-full mt-3" />
              )}
            </div>

            {/* Clasificación rápida */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <p className="text-white font-medium mb-3">¿De qué va?</p>
              <div className="grid grid-cols-6 gap-2">
                {Object.entries(AUDIO_COLORS).slice(0, 12).map(([key, c]) => (
                  <button
                    key={key}
                    onClick={() => setColor(key)}
                    className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                      color === key ? 'ring-2 ring-green-400 bg-green-500/20' : 'bg-white/5'
                    }`}
                  >
                    <span 
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                      style={{ backgroundColor: c.hex }}
                    >
                      {c.emoji}
                    </span>
                    <span className="text-white/60 text-[9px] mt-1">{c.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Emoji rápido */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <p className="text-white font-medium mb-3">Emoji</p>
              <div className="flex flex-wrap gap-2">
                {['😂', '🔥', '💡', '🎵', '📢', '❤️', '👍', '🎯', '⚽', '🍴'].map((e) => (
                  <button
                    key={e}
                    onClick={() => setEmoji(e)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${
                      emoji === e ? 'bg-green-500/50 ring-2 ring-green-400' : 'bg-white/5'
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            {/* Botón publicar */}
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-4 rounded-2xl font-bold text-lg disabled:opacity-50"
            >
              {uploading ? 'Subiendo...' : '🚀 Publicar en AudioToo'}
            </button>
          </>
        )}
      </main>
    </div>
  );
};

export default CompartirPage;
